import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpEventType, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, filter, retry, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../api/api.service';
import { LoggerService } from '../../logger-services/logger.service';
import { ConfigurationService } from '../../configuration/configuration.service';
import { DownloadPresignedurlService } from '../download-presignedurl-service/download-presignedurl.service';
import { 
  PresignUrlRequest, 
  PresignUrlResponse, 
  SaveToMyFilesRequest, 
  SaveToMyFilesResponse
} from '../../../app/models/file-upload.models';
import { TokenService } from '../../../shared/services/token/token.service';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly loggerService = inject(LoggerService);
  private readonly configService = inject(ConfigurationService);
  private readonly downloadPresignedUrlService = inject(DownloadPresignedurlService);
  private readonly tokenService = inject(TokenService);
  
  /**
   * Gets the configuration data from the configuration service
   */
  private get configdata(): any {
    return this.configService.configData;
  }

  /**
   * Get CORS-compliant headers for presigned URF URL APIs
   */
  private getCorURFHeaders(token?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'content-type': 'application/json',
      'user-id': (environment.LoginUserID == undefined || environment.LoginUserID == null) ? "" : environment.LoginUserID,
      'tenant-id': environment.TenantId || '0',
      'domain-name': environment.DomainName || '',
    });

    // Add user full name if available
    if (environment.LoginUserName) {
      headers = headers.set('x-user-full-name', environment.LoginUserName);
    }

    // Add authorization token if provided
    if (token) {
      headers = headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Generates presigned URL for a single file upload to S3
   * @param fileName - Name of the file with extension
   * @returns Observable containing presigned URL response
   */
  generatePresignedUrl(fileName: string): Observable<PresignUrlResponse> {
    try {
      const endpoint = this.apiService.getApiUrlFromConfig('GENERATE_PRESIGNED_URL');
      const requestPayload: PresignUrlRequest = {
        fileName: fileName
      };

      // Use direct HTTP call with CORS-compliant headers
      const url = `${this.configdata.docFileUploadUrl}${endpoint}`;
      const headers = this.getCorURFHeaders(this.tokenService.getAccessToken());

      return this.http.post<PresignUrlResponse>(url, requestPayload, { headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          this.loggerService.logError(this.formatError(error), "generatePresignedUrl");
          return throwError(() => error);
        })
      );
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), "generatePresignedUrl");
      return throwError(() => error);
    }
  }

  /**
   * Saves uploaded file to My Files
   * @param fileName - Name of the file with extension
   * @param s3Key - S3 object key obtained from presign-upload-url endpoint
   * @returns Observable containing save response
   */
  saveToMyFiles(fileName: string, s3Key: string): Observable<SaveToMyFilesResponse> {
    try {
      const endpoint = this.apiService.getApiUrlFromConfig('SAVE_TO_MY_FILES');
      const requestPayload: SaveToMyFilesRequest = {
        fileName: fileName,
        s3Key: s3Key
      };

      // Use direct HTTP call with CORS-compliant headers
      const url = `${this.configdata.docFileUploadUrl}${endpoint}`;
      const headers = this.getCorURFHeaders(this.tokenService.getAccessToken());

      return this.http.post<SaveToMyFilesResponse>(url, requestPayload, { headers }).pipe(
        catchError((error: HttpErrorResponse) => {
          this.loggerService.logError(this.formatError(error), "saveToMyFiles");
          return throwError(() => error);
        })
      );
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), "saveToMyFiles");
      return throwError(() => error);
    }
  }

  /**
   * Uploads a file to S3 using presigned URL
   * @param file - The file to upload
   * @param presignedUrl - The presigned URL for upload
   * @returns Observable containing upload progress and completion
   */
  uploadFileToS3(file: File, presignedUrl: string): Observable<HttpEvent<any>> {
    try {
      // Use XMLHttpRequest directly to avoid Angular's automatic headers
      return new Observable<HttpEvent<any>>(observer => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            observer.next({
              type: HttpEventType.UploadProgress,
              loaded: event.loaded,
              total: event.total
            } as HttpEvent<any>);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            observer.next({
              type: HttpEventType.Response,
              status: xhr.status,
              statusText: xhr.statusText,
              body: xhr.responseText
            } as HttpEvent<any>);
            observer.complete();
          } else {
            observer.error(new HttpErrorResponse({
              error: xhr.responseText,
              status: xhr.status,
              statusText: xhr.statusText,
              url: presignedUrl
            }));
          }
        });

        xhr.addEventListener('error', () => {
          observer.error(new HttpErrorResponse({
            error: 'Network error',
            status: 0,
            statusText: 'Unknown Error',
            url: presignedUrl
          }));
        });

        xhr.open('PUT', presignedUrl);
        // Don't set any headers - let the browser handle only what's necessary
        xhr.send(file);
      }).pipe(
        retry({
          count: 2,
          delay: (error: any, retryCount: number) => {
            // Only retry on network errors or 5xx server errors
            if (error instanceof HttpErrorResponse) {
              if (error.status === 0 || (error.status >= 500 && error.status < 600)) {
                const delayMs = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s...
                return timer(delayMs);
              }
            }
            // Don't retry on other errors (4xx, etc.)
            return throwError(() => error);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.loggerService.logError(this.formatError(error), "uploadFileToS3");
          return throwError(() => error);
        })
      );
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), "uploadFileToS3");
      return throwError(() => error);
    }
  }

  /**
   * Complete file upload process: generate presigned URLs, upload to S3, save to My Files, and get download URL
   * @param filesWithDocIds - Array of files with their doc IDs to upload
   * @param userId - User identifier (kept for backward compatibility but not used in new API)
   * @param tenantId - Tenant identifier (kept for backward compatibility but not used in new API)
   * @returns Promise containing upload results
   */
  async uploadFiles(filesWithDocIds: Array<{file: File, docId: string}>, userId: string, tenantId: string): Promise<{
    success: boolean;
    uploadedFiles: { 
      file: File; 
      s3Key: string; 
      docId: string; 
      uploadSuccess: boolean; 
      documentId?: number;
      presignedDownloadUrl?: string;
      fileName?: string;
      fileSize?: number;
      fileType?: string;
      version?: string;
    }[];
    errors: string[];
  }> {
    try {
      // Process files in parallel using the four-step API
      const uploadPromises = filesWithDocIds.map(async ({file, docId}) => {
        try {
          // Step 1: Generate presigned URL for this file
          const presignedResponse = await this.generatePresignedUrl(file.name).toPromise();
          
          if (!presignedResponse || presignedResponse.code !== 200 || !presignedResponse.data) {
            return {
              file,
              s3Key: '',
              docId: docId,
              uploadSuccess: false,
              documentId: undefined,
              error: `Failed to generate presigned URL for ${file.name}: ${presignedResponse?.msg || 'Unknown error'}`
            };
          }

          const { presignatureUrl, s3Key } = presignedResponse.data;

          // Step 2: Upload file to S3 using presigned URL
          try {
            await this.uploadFileToS3WithRetry(
              file, 
              presignatureUrl,
              3 // Max 3 retry attempts
            );
          } catch (uploadError: any) {
            this.loggerService.logError(
              this.formatError(uploadError), 
              "uploadFiles - S3 upload failed"
            );
            return {
              file,
              s3Key: s3Key,
              docId: docId,
              uploadSuccess: false,
              documentId: undefined,
              error: `S3 upload error for ${file.name}: ${uploadError.message || 'Unknown error'}`
            };
          }

          // Step 3: Save document to My Files
          try {
            const saveResponse = await this.saveToMyFiles(file.name, s3Key).toPromise();
            
            if (!saveResponse || saveResponse.code !== 200 || !saveResponse.data) {
              return {
                file,
                s3Key: s3Key,
                docId: docId,
                uploadSuccess: false,
                documentId: undefined,
                error: `Failed to save ${file.name} to My Files: ${saveResponse?.msg || 'Unknown error'}`
              };
            }

            // Step 4: Get presigned download URL for the uploaded document
            try {
              const downloadUrlResponse = await this.downloadPresignedUrlService.getPresignedDownloadUrl(saveResponse.data.documentId).toPromise();
              
              if (!downloadUrlResponse || downloadUrlResponse.code !== 200 || !downloadUrlResponse.data) {
                this.loggerService.logWarning(
                  `Failed to get presigned download URL for ${file.name}: ${downloadUrlResponse?.msg || 'Unknown error'}`,
                  "uploadFiles - download URL generation"
                );
                // Non-critical error, continue with upload success
              } else {
                this.loggerService.logInfo(
                  `Presigned download URL generated for ${file.name}: ${downloadUrlResponse.data.presignedUrl}`,
                  "uploadFiles"
                );
              }

              return {
                file,
                s3Key: s3Key,
                docId: saveResponse.data.documentId.toString(),
                uploadSuccess: true,
                documentId: saveResponse.data.documentId,
                presignedDownloadUrl: downloadUrlResponse?.data?.presignedUrl,
                fileName: downloadUrlResponse?.data?.fileName,
                fileSize: downloadUrlResponse?.data?.fileSize,
                fileType: downloadUrlResponse?.data?.fileType,
                version: downloadUrlResponse?.data?.version,
                error: null
              };
            } catch (downloadUrlError: any) {
              this.loggerService.logWarning(
                `Error getting presigned download URL for ${file.name}: ${downloadUrlError.message || 'Unknown error'}`,
                "uploadFiles - download URL generation"
              );
              // Non-critical error, continue with upload success
              return {
                file,
                s3Key: s3Key,
                docId: saveResponse.data.documentId.toString(),
                uploadSuccess: true,
                documentId: saveResponse.data.documentId,
                error: null
              };
            }
          } catch (saveError: any) {
            this.loggerService.logError(
              this.formatError(saveError), 
              "uploadFiles - save to My Files failed"
            );
            return {
              file,
              s3Key: s3Key,
              docId: docId,
              uploadSuccess: false,
              documentId: undefined,
              error: `Failed to save ${file.name} to My Files: ${saveError.message || 'Unknown error'}`
            };
          }

        } catch (error: any) {
          this.loggerService.logError(
            this.formatError(error), 
            "uploadFiles - individual file processing"
          );
          return {
            file,
            s3Key: '',
            docId: docId,
            uploadSuccess: false,
            documentId: undefined,
            error: `Error processing ${file.name}: ${error.message || 'Unknown error'}`
          };
        }
      });

      // Wait for all uploads to complete (parallel execution)
      const results = await Promise.allSettled(uploadPromises);

      // Process results
      const uploadResults: { 
        file: File; 
        s3Key: string; 
        docId: string; 
        uploadSuccess: boolean; 
        documentId?: number;
        presignedDownloadUrl?: string;
        fileName?: string;
        fileSize?: number;
        fileType?: string;
        version?: string;
      }[] = [];
      const errors: string[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const uploadResult = result.value;
          uploadResults.push({
            file: uploadResult.file,
            s3Key: uploadResult.s3Key,
            docId: uploadResult.docId,
            uploadSuccess: uploadResult.uploadSuccess,
            documentId: uploadResult.documentId,
            presignedDownloadUrl: uploadResult.presignedDownloadUrl,
            fileName: uploadResult.fileName,
            fileSize: uploadResult.fileSize,
            fileType: uploadResult.fileType,
            version: uploadResult.version
          });
          if (uploadResult.error) {
            errors.push(uploadResult.error);
          }
        } else {
          // Promise was rejected
          errors.push(`Upload promise rejected: ${result.reason}`);
        }
      });

      const allUploadsSuccessful = uploadResults.every(result => result.uploadSuccess);

      return {
        success: allUploadsSuccessful,
        uploadedFiles: uploadResults,
        errors: errors
      };

    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), "uploadFiles");
      return {
        success: false,
        uploadedFiles: [],
        errors: [`File upload process failed: ${error.message}`]
      };
    }
  }

  /**
   * Uploads a file to S3 with retry logic for robustness
   * @param file - The file to upload
   * @param presignedUrl - The presigned URL for upload
   * @param maxRetries - Maximum number of retry attempts
   * @returns Promise that resolves when upload is successful
   */
  private async uploadFileToS3WithRetry(
    file: File, 
    presignedUrl: string, 
    maxRetries: number = 3
  ): Promise<void> {
    let lastError: any;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Exponential backoff: wait before retry (0ms, 1s, 2s, 4s...)
        if (attempt > 0) {
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        // Increase timeout based on file size (minimum 30s, +10s per MB)
        const fileSizeMB = file.size / (1024 * 1024);
        const dynamicTimeout = Math.max(30000, 30000 + (fileSizeMB * 10000));

        const uploadResponse = await this.uploadFileToS3(file, presignedUrl)
          .pipe(
            filter(event => event.type === HttpEventType.Response),
            timeout(dynamicTimeout)
          )
          .toPromise();

        if (uploadResponse) {
          return; // Success
        } else {
          throw new Error('Upload response was null or undefined');
        }
      } catch (error: any) {
        lastError = error;
        
        // Log retry attempt
        if (attempt < maxRetries - 1) {
          this.loggerService.logError(
            this.formatError(error),
            `uploadFileToS3WithRetry - Attempt ${attempt + 1}/${maxRetries} failed for ${file.name}, retrying...`
          );
        }
      }
    }

    // All retries exhausted
    throw lastError || new Error(`Upload failed after ${maxRetries} attempts`);
  }

  /**
   * Validates files before upload according to API requirements
   * @param files - Array of files to validate
   * @returns Validation result with errors if any
   */
  validateFilesForUpload(files: File[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const config = this.configdata.FileUploadConfig;

    // Check file count
    if (files.length > (config?.maxFiles || 3)) {
      errors.push(`Maximum ${config?.maxFiles || 3} files allowed per request`);
    }

    // Check for duplicate file names
    const fileNames = files.map(f => f.name);
    const duplicates = fileNames.filter((name, index) => fileNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate file names are not allowed: ${duplicates.join(', ')}`);
    }

    // Validate each file
    files.forEach(file => {
      // Check file type
      const allowedTypes = config?.allowedTypes || [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      const allowedExtensions = config?.allowedExtensions || ['.pdf', '.docx', '.txt'];
      
      const isValidType = allowedTypes.includes(file.type) || 
                         allowedExtensions.some((ext: string) => file.name.toLowerCase().endsWith(ext));
      
      if (!isValidType) {
        errors.push(`File extension must be one of: .txt, .docx, .pdf (${file.name})`);
      }

      // Check file size based on type
      if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
        const maxSizeTxt = config?.maxFileSizeTXT || 1048576; // 1MB
        if (file.size > maxSizeTxt) {
          errors.push(`Text file size cannot exceed 1 MB. Current size: ${(file.size / 1048576).toFixed(1)} MB (${file.name})`);
        }
      } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ||
                 file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 file.name.toLowerCase().endsWith('.docx')) {
        const maxSizePdfDocx = config?.maxFileSizePDF || 3145728; // 3MB
        if (file.size > maxSizePdfDocx) {
          errors.push(`PDF/DOCX file size cannot exceed 3 MB. Current size: ${(file.size / 1048576).toFixed(1)} MB (${file.name})`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Formats error object for logging
   * @param error - The error object
   * @returns Formatted error object
   */
  private formatError(error: any): any {
    if (error instanceof HttpErrorResponse) {
      return {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        error: error.error
      };
    }
    return error;
  }
}
